import { createFileRoute, Link } from '@tanstack/react-router'
import { useEffect } from 'react'
import { COLORS, FONTS, sectionInner } from '@/components/home/tokens'
import { BlogPostBody, MEDIA_WIDTH, PROSE_WIDTH } from '@/components/blog/BlogPostBody'
import { LISTED_BLOG_POSTS, formatAuthors, formatByline, getBlogPost, type BlogPost } from '@/lib/blogPosts'
import { applyBlogMeta, blogPostMeta } from '@/lib/seo'

export const Route = createFileRoute('/blog_/$slug')({
  component: BlogPostPage,
})

// Local filenames resolve against /images/blog/; absolute/protocol-relative
// URLs (e.g. HubSpot's CDN) are used as-is.
const ASSET = (src: string) =>
  /^(https?:)?\/\//.test(src) ? src : `${import.meta.env.BASE_URL}images/blog/${src}`

/** Centered media-width track (hero, body, footer all share this measure). */
const mediaTrack: React.CSSProperties = {
  width: '100%',
  maxWidth: MEDIA_WIDTH,
  margin: '0 auto',
}

/** Narrow reading measure, centered, for header text. */
const proseTrack: React.CSSProperties = {
  width: '100%',
  maxWidth: PROSE_WIDTH,
  margin: '0 auto',
}

function RelatedCard({ post }: { post: BlogPost }) {
  return (
    <Link
      to="/blog/$slug/"
      params={{ slug: post.slug }}
      style={{ display: 'flex', flexDirection: 'column', gap: 16, textDecoration: 'none' }}
    >
      <div style={{ aspectRatio: '16 / 10', borderRadius: 10, overflow: 'hidden' }}>
        <img
          src={ASSET(post.image)}
          alt={post.title}
          loading="lazy"
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </div>
      <h3
        style={{
          fontFamily: FONTS.body,
          fontWeight: 500,
          fontSize: 'var(--text-lg)',
          lineHeight: 1.2,
          color: COLORS.neutral1,
          margin: 0,
        }}
      >
        {post.title}
      </h3>
      <p style={{ fontFamily: FONTS.body, fontSize: 'var(--text-sm)', color: COLORS.neutral5, margin: 0 }}>
        {formatAuthors(post.authors)}
      </p>
    </Link>
  )
}

function BlogPostPage() {
  const { slug } = Route.useParams()
  const post = getBlogPost(slug)

  // On client-side navigation, sync the document head to this post (title,
  // description, canonical, and article OG/Twitter tags). On first load the
  // prerendered HTML already carries these — see scripts/prerender.ts.
  useEffect(() => {
    if (post) applyBlogMeta(blogPostMeta(post))
    else document.title = 'Blog — Ent'
  }, [post])

  if (!post) {
    return (
      <section style={{ position: 'relative', zIndex: 10, padding: '200px 0 160px' }}>
        <div style={{ ...sectionInner }}>
          <div style={proseTrack}>
            <Link to="/blog/" style={backLinkStyle}>
              ← Back to Blog
            </Link>
            <p style={{ fontFamily: FONTS.body, fontSize: 'var(--text-lg)', color: COLORS.neutral2, marginTop: 32 }}>
              That post could not be found.
            </p>
          </div>
        </div>
      </section>
    )
  }

  const related = LISTED_BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 3)
  const metaLine = [formatByline(post.authors), post.readMinutes && `${post.readMinutes} min read`]
    .filter(Boolean)
    .join('  ·  ')

  return (
    <section style={{ position: 'relative', zIndex: 10, padding: '0 0 140px' }}>
      {/* ── Full-bleed cover image at the very top of the page ── */}
      <div style={{ width: '100%', height: 'clamp(320px, 56vh, 640px)', overflow: 'hidden' }}>
        <img
          src={ASSET(post.image)}
          alt={post.title}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </div>

      <div style={{ ...sectionInner }}>
        {/* ── Header: title → meta ── */}
        {/* The excerpt is intentionally not rendered here as a standfirst: it
            often restates the article's opening paragraph, which reads as
            duplicated content. It still drives the index cards and SEO meta. */}
        <div style={{ ...proseTrack, marginTop: 72 }}>
          <h1
            style={{
              fontFamily: FONTS.heading,
              fontWeight: 200,
              fontSize: 'var(--text-4xl)',
              lineHeight: 1.08,
              letterSpacing: '-0.02em',
              color: COLORS.neutral2,
              margin: 0,
            }}
          >
            {post.title}
          </h1>

          <p
            style={{
              fontFamily: FONTS.mono,
              fontSize: 'var(--text-sm)',
              color: COLORS.green1,
              margin: '28px 0 0',
            }}
          >
            {metaLine}
          </p>
        </div>

        {/* ── Body ── */}
        <div style={{ marginTop: 104 }}>
          {post.content ? (
            <BlogPostBody content={post.content} />
          ) : (
            <p
              style={{
                ...proseTrack,
                fontFamily: FONTS.body,
                fontSize: 'var(--text-base)',
                lineHeight: 1.7,
                color: COLORS.neutral2,
              }}
            >
              Full article coming soon.
            </p>
          )}
        </div>

        {/* ── Related articles ── */}
        {related.length > 0 && (
          <div style={{ ...mediaTrack, marginTop: 120 }}>
            <hr style={{ border: 0, borderTop: `1px solid ${COLORS.neutral5}`, opacity: 0.25, margin: '0 0 48px' }} />
            <h2
              style={{
                fontFamily: FONTS.heading,
                fontWeight: 200,
                fontSize: 'var(--text-2xl)',
                letterSpacing: '-0.02em',
                color: COLORS.neutral2,
                margin: '0 0 40px',
              }}
            >
              More from the blog
            </h2>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 32,
              }}
            >
              {related.map((p) => (
                <RelatedCard key={p.slug} post={p} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

const backLinkStyle: React.CSSProperties = {
  fontFamily: FONTS.body,
  fontSize: 'var(--text-sm)',
  fontWeight: 500,
  color: COLORS.green1,
  textDecoration: 'none',
}
